import {
  IArpFavoritedNotification,
  IArpUsedNotification,
  INotificationUnion,
  IParentArpFavoritedNotification,
  NotificationEnum
} from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { useMarkNotificationAsRead } from '../../../features/notifications'
import { styled } from '../../lib/stitches'
import ProfileRegistry from '../../stores/registry/ProfileRegistry'
import { CheckboxGreen, Heart, Share } from '../core/icons'
import Text from '../core/Text/Text'
import ProfileAvatar from '../platform/explore/common/ProfileAvatar'
import { useAsyncEffect } from '../platform/explore/common/utils'

const NotificationWrapper = styled('div', {
  display: 'flex',
  marginBottom: '10px',
  alignItems: 'center',
  gap: '13px',
  '&:hover': {
    cursor: 'pointer',
    opacity: 0.7
  }
})

const NotificationIcon = styled('div', {
  position: 'absolute',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  bottom: '-1px',
  right: '-3px'
})

const NotificationAvatar = styled('div', {
  width: '43px',
  height: '43px',
  borderRadius: '50%',
  background: '#D9D9D9',
  position: 'relative',
  '> img': {
    width: '43px',
    height: '43px',
    borderRadius: '50%'
  }
})

const NotificationContent = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  marginRight: '12px'
})

const DisplayName = styled(Link, {
  color: '$indigo05',
  fontSize: '13px',
  fontWeight: 'bold'
})

const Title = styled('span', {
  color: '$indigo05',
  fontWeight: 'bold'
})

const NotificationStatus = styled('div', {
  width: '9px',
  height: '9px',
  borderRadius: '50%',
  background: '#E84AD8',
  marginRight: '12px'
})

const segmentNotificationClick = (type: string) => {
  window.analytics.track('Button Clicked', {
    button: 'Notification',
    type: type
  })
}

interface IProps {
  id: number
  type: NotificationEnum
  arpId: number
  readAt: string
  createdAt: string
  data: INotificationUnion
}

const Notification = ({ id, type, arpId, readAt, createdAt, data }: IProps) => {
  const navigate = useNavigate()
  const markNotificationAsRead = useMarkNotificationAsRead()
  const [arpMedia, setArpMedia] = useState<IArpMedia | null>(null)
  const [parentArpMedia, setParentArpMedia] = useState<IArpMedia | null>(null)
  const [isRead, setIsRead] = useState(readAt !== null)

  useEffect(() => {
    return () => {
      if (readAt === null) {
        markNotificationAsRead.mutate({ id })
      }
    }
  }, [])

  useAsyncEffect(async () => {
    const arpMediaData = await ArpRegistry.getByIdAsync(arpId)
    setArpMedia(arpMediaData)
  }, [arpId])

  useAsyncEffect(async () => {
    const { parentArpId } = data as IParentArpFavoritedNotification
    const { triggeredByArpId } = data as IArpUsedNotification

    const id = parentArpId || triggeredByArpId

    if (id) {
      const parentArpMediaData = await ArpRegistry.getByIdAsync(id)
      setParentArpMedia(parentArpMediaData)
    }
  }, [data])

  if (!arpMedia) {
    return null
  }

  const { triggeredByWalletAddress } = data as IArpFavoritedNotification

  const profile = ProfileRegistry.getByIndexReactive(triggeredByWalletAddress)
  const userDisplayName = profile?.display_name || 'Someone'
  const mediaType = arpMedia.type === 'song' ? 'song' : 'sound'
  const parentMediaType = parentArpMedia?.type === 'song' ? 'song' : 'sound'

  return (
    <NotificationWrapper
      key={id}
      onClick={(event: React.MouseEvent) => {
        if (readAt === null) {
          markNotificationAsRead.mutate({ id })
          setIsRead(true)
        }
        segmentNotificationClick(type)

        // @ts-expect-error
        const { href } = event.target
        const arpToNavigateTo = NotificationEnum.ARP_USED === type ? parentArpMedia : arpMedia

        if (!href || href === '') {
          navigate(`/${arpToNavigateTo.type}?token=${arpToNavigateTo.id}`)
        }
      }}
    >
      <NotificationAvatar>
        <ProfileAvatar eth={triggeredByWalletAddress} css={{ width: '43px', height: '43px' }} />
        <NotificationIcon>
          {NotificationEnum.MINT_COMPLETED === type && <CheckboxGreen />}
          {NotificationEnum.ARP_USED === type && <Share />}
          {[NotificationEnum.ARP_FAVORITED, NotificationEnum.PARENT_ARP_FAVORITED].includes(
            type
          ) && <Heart />}
        </NotificationIcon>
      </NotificationAvatar>
      <NotificationContent>
        <Text
          size="12"
          fontWeight="medium"
          css={{
            lineHeight: '20px',
            color: '$gray15',
            letterSpacing: '-0.02em'
          }}
        >
          {NotificationEnum.MINT_COMPLETED === type && (
            <>
              Your {mediaType} <Title>{arpMedia.metadata.title}</Title> has finished minting
            </>
          )}

          {NotificationEnum.ARP_FAVORITED === type && (
            <>
              <DisplayName className="link" to={`/profile/${triggeredByWalletAddress}`}>
                {userDisplayName}
              </DisplayName>{' '}
              favorited your {mediaType} {arpMedia.metadata.title}
            </>
          )}

          {NotificationEnum.PARENT_ARP_FAVORITED === type && (
            <>
              <DisplayName className="link" to={`/profile/${triggeredByWalletAddress}`}>
                {userDisplayName}
              </DisplayName>{' '}
              favorited {parentArpMedia?.metadata?.title} which uses your {mediaType}{' '}
              {arpMedia.metadata.title}
            </>
          )}

          {NotificationEnum.ARP_USED === type && (
            <>
              <DisplayName className="link" to={`/profile/${triggeredByWalletAddress}`}>
                {userDisplayName}
              </DisplayName>{' '}
              used your {mediaType} {arpMedia.metadata.title} in their {parentMediaType},{' '}
              {parentArpMedia?.metadata?.title}
            </>
          )}
        </Text>
        <Text
          size="10"
          fontWeight="regular"
          css={{
            lineHeight: '20px',
            color: '$gray07',
            letterSpacing: '-0.02em'
          }}
        >
          {moment(createdAt).fromNow()}
        </Text>
      </NotificationContent>
      {readAt === null && !isRead && <NotificationStatus />}
    </NotificationWrapper>
  )
}

export default observer(Notification)
